import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAgentPermission } from '@entities/user';
import {
  useGetPtoProjectQuery,
  useListPtoProjectsQuery,
  useListPtoRdImportsQuery,
} from '@shared/api';
import { Table } from '@shared/ui/Table';
import { errorText } from './errors';
import { RdQueuePanel } from './RdQueuePanel';
import { PROJECTS_LIMIT, ProjectPicker, projectLabel } from './ProjectPicker';
import { UploadBox } from './UploadBox';
import { useRdUpload } from './useRdUpload';
import { isWorking, statusLabel, StatusTone } from './status';
import { Cell, ErrorText, Muted, Panel, Section, SectionTitle, Small, TargetBox } from './styles';

/**
 * Загрузка РД архивом (docs/pto-rd-import-spec.md): архив или папка с РД на
 * весь объект → раскладка по титулам, комплектам и ревизиям → проверка →
 * загрузка в реестр. Здесь — новый пакет, мои пакеты и очередь обработки РД.
 */
export function PtoRdImportHome() {
  const { can_write } = useAgentPermission('pto');
  const [searchParams] = useSearchParams();
  const contextProjectId = searchParams.get('projectId');
  // На общей странице цель всегда выбирает инженер; первый проект не выбираем.
  const [pickedProjectId, setPickedProjectId] = useState<string | null>(null);
  const projectId = contextProjectId || pickedProjectId;
  const {
    data: projectsData,
    isFetching: projectsLoading,
    error: projectsError,
  } = useListPtoProjectsQuery(
    { limit: PROJECTS_LIMIT },
    { skip: !can_write || Boolean(contextProjectId) },
  );
  const {
    currentData: projectData,
    error: projectError,
    isFetching: projectLoading,
  } = useGetPtoProjectQuery(projectId ?? '', { skip: !projectId });
  const upload = useRdUpload(projectData?.project.id);
  const [poll, setPoll] = useState(0);
  const { data, error, isLoading } = useListPtoRdImportsQuery(undefined, {
    // Список при каждом открытии свежий; пока есть пакеты в работе — опрашиваем.
    refetchOnMountOrArgChange: true,
    pollingInterval: poll,
    skipPollingIfUnfocused: true,
  });
  const items = data?.items ?? [];
  const anyWorking = items.some((i) => isWorking(i.status));
  useEffect(() => setPoll(anyWorking ? 10000 : 0), [anyWorking]);

  return (
    <div>
      <h1>Загрузка РД архивом</h1>
      {can_write ? (
        <>
          <Panel>
            <TargetBox>
              <strong>Проект для загрузки РД</strong>
              {contextProjectId ? null : (
                <ProjectPicker
                  projectId={pickedProjectId}
                  projects={projectsData?.items ?? []}
                  loading={projectsLoading}
                  locked={upload.busy}
                  onPick={setPickedProjectId}
                />
              )}
              {projectData ? (
                <Link to={`/agents/pto/${projectData.project.id}`}>
                  {projectLabel(projectData.project)}
                </Link>
              ) : projectId && projectLoading ? (
                <Muted>Загрузка проекта…</Muted>
              ) : null}
              <Muted>
                Вся РД из пакета будет загружена только в выбранный проект.
                {!projectId ? ' Сначала выберите проект, затем архив или папку.' : ''}
              </Muted>
              {projectError ? (
                <ErrorText>
                  {errorText(projectError, 'Не удалось открыть выбранный проект.')}
                </ErrorText>
              ) : null}
              {projectsError ? (
                <ErrorText>
                  {errorText(projectsError, 'Не удалось загрузить список проектов.')}
                </ErrorText>
              ) : null}
            </TargetBox>
          </Panel>
          {projectData ? (
            <Section>
              <UploadBox
                key={projectData.project.id}
                onFiles={(files) => void upload.start(files)}
                busy={upload.busy}
                stage={upload.stage}
                progress={upload.progress}
                warnings={upload.warnings}
                error={upload.error}
                onCancel={upload.cancel}
              />
            </Section>
          ) : null}
        </>
      ) : (
        <Muted>Загрузка доступна инженерам с правом записи в ОСНОВА.Инженер.</Muted>
      )}

      <RdQueuePanel />

      <Section>
        <SectionTitle>Пакеты загрузки</SectionTitle>
        <Panel>
          {isLoading ? (
            <Muted>Загрузка списка…</Muted>
          ) : error && !data ? (
            <ErrorText>{errorText(error, 'Не удалось загрузить список пакетов.')}</ErrorText>
          ) : items.length === 0 ? (
            <Muted>Пакетов пока нет.</Muted>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Архив или папка</th>
                  <th>Состояние</th>
                  <th>Создан</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id}>
                    <Cell>
                      <Link to={`/agents/pto/rd-import/${i.id}`}>{i.source_name}</Link>
                    </Cell>
                    <Cell>
                      <StatusTone status={i.status}>{statusLabel(i.status)}</StatusTone>
                      {i.status === 'failed' && i.error ? (
                        <div>
                          <Small>{i.error}</Small>
                        </div>
                      ) : null}
                    </Cell>
                    <Cell>
                      <Small>{new Date(i.created_at).toLocaleString('ru-RU')}</Small>
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      </Section>
    </div>
  );
}
